import { createSlice } from '@reduxjs/toolkit';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { TcServiceContract, TcServiceContractLog } from '../../types/tc-service-contract/tc-service-contract.type';
import { getContractDetails, getContractsList, getContractLogs } from './tcServiceContracts.action';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';

export interface TcServiceContractsState {
  contractsList: null | PaginationType<TcServiceContract>;
  contractsListFetchStatus: FETCH_STATUS;
  contractsRouteParams: RouteParamsType;
  contractDetails: null | TcServiceContract;
  contractDetailsFetchStatus: FETCH_STATUS;
  contractLogs: TcServiceContractLog[];
  contractLogsFetchStatus: FETCH_STATUS;
}

export const initialContractsRouteParams: any = {
  page: 1,
  limit: 10,
  order: '-tc_service_contracts.id',
  q: '',
  status: '',
  contractType: '',
};

const INIT_STATE: TcServiceContractsState = {
  contractsList: null,
  contractsListFetchStatus: FETCH_STATUS.NULL,
  contractsRouteParams: initialContractsRouteParams,
  contractDetails: null,
  contractDetailsFetchStatus: FETCH_STATUS.NULL,
  contractLogs: [],
  contractLogsFetchStatus: FETCH_STATUS.NULL,
};

export const tcServiceContractsSlice = createSlice({
  name: 'tcServiceContracts',
  initialState: INIT_STATE,
  reducers: {
    setContractsList: (state, action) => {
      state.contractsList = action.payload;
    },
    setContractsListFetchStatus: (state, action) => {
      state.contractsListFetchStatus = action.payload;
    },
    setContractsRouteParams: (state, action) => {
      state.contractsRouteParams = { ...state.contractsRouteParams, ...action.payload };
    },
    setContractDetails: (state, action) => {
      state.contractDetails = action.payload;
    },
    setContractDetailsFetchStatus: (state, action) => {
      state.contractDetailsFetchStatus = action.payload;
    },
    setContractLogs: (state, action) => {
      state.contractLogs = action.payload;
    },
    setContractLogsFetchStatus: (state, action) => {
      state.contractLogsFetchStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'contracts') {
        state.contractsRouteParams = { ...state.contractsRouteParams, ...payload.data } as any;
      }
    });

    builder.addCase(getContractsList.pending, state => {
      state.contractsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getContractsList.rejected, state => {
      state.contractsListFetchStatus = FETCH_STATUS.REJECTED;
    });
    builder.addCase(getContractsList.fulfilled, (state, action) => {
      state.contractsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.contractsList = action.payload;
    });

    builder.addCase(getContractDetails.pending, state => {
      state.contractDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getContractDetails.rejected, (state, action) => {
      state.contractDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.contractDetails = null;
      snackbarUtils.error(action.payload);
    });
    builder.addCase(getContractDetails.fulfilled, (state, action) => {
      state.contractDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.contractDetails = action.payload;
    });

    // Contract Logs
    builder.addCase(getContractLogs.pending, state => {
      state.contractLogsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getContractLogs.rejected, state => {
      state.contractLogsFetchStatus = FETCH_STATUS.REJECTED;
    });
    builder.addCase(getContractLogs.fulfilled, (state, action) => {
      state.contractLogsFetchStatus = FETCH_STATUS.FULFILLED;
      state.contractLogs = action.payload;
    });
  },
});

export const { setContractsList, setContractsListFetchStatus } = tcServiceContractsSlice.actions;

export default tcServiceContractsSlice.reducer;
